import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useRealtime } from "@/contexts/RealtimeContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, MapPin, Radio } from "lucide-react";
import { toast } from "sonner";

const ROLE_TONE = {
  resident: "text-emerald-300 bg-emerald-500/[0.08] border-emerald-500/30",
  admin:    "text-orange-300  bg-orange-500/[0.08]  border-orange-500/30",
  vendor:   "text-indigo-300  bg-indigo-500/[0.08]  border-indigo-500/30",
  board:    "text-violet-300  bg-violet-500/[0.08]  border-violet-500/30",
};

/**
 * OccupancyMapCard — live snapshot of which communities have residents /
 * admins / vendors / directors online right now. Backfills via
 * /api/admin/realtime/occupancy on mount, then re-fetches whenever a
 * presence.join / presence.leave event arrives on any subscribed topic.
 */
export function OccupancyMapCard() {
  const { user } = useAuth();
  const { subscribe, presence, status } = useRealtime();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/realtime/occupancy");
      setSnapshot(data);
    } catch (e) {
      if (e?.response?.status !== 403 && e?.response?.status !== 401) {
        toast.error(apiErrorMessage(e) || "Failed to load occupancy");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-subscribe to presence for every property the user can see, so
  // join/leave events fire and we can lazily re-poll the snapshot.
  useEffect(() => {
    if (!user || !user.property_ids) return;
    const unsubs = user.property_ids.map((pid) =>
      subscribe(`presence:${pid}`, () => {
        // Debounced refresh: only refetch every 1.5s at most
        if (!window.__pp_occ_deb || Date.now() - window.__pp_occ_deb > 1500) {
          window.__pp_occ_deb = Date.now();
          load();
        }
      }),
    );
    return () => unsubs.forEach((u) => u && u());
  }, [user, subscribe, load]);

  // Merge in local presence context so numbers update instantly even before
  // the snapshot re-poll completes.
  const communities = (snapshot?.communities || []).map((c) => {
    const localUsers = presence[c.property_id];
    if (localUsers && localUsers.length !== c.online_count) {
      return { ...c, online_count: localUsers.length,
               users: localUsers.map((u) => ({ user_id: u.user_id, name: u.name, role: u.role, since: u.since })) };
    }
    return c;
  });
  const totalOnline = communities.reduce((s, c) => s + c.online_count, 0);

  return (
    <Card className="p-4 border-white/10 bg-card" data-testid="occupancy-map-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline flex items-center gap-2">
            Realtime occupancy
            <span
              className={"h-1.5 w-1.5 rounded-full " +
                (status === "online" ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]" : "bg-zinc-500")}
              data-testid={`occupancy-status-${status}`}
            />
          </div>
          <div className="text-base font-semibold font-display mt-0.5 inline-flex items-center gap-2">
            <Users className="h-4 w-4 text-emerald-400" /> Who&apos;s online right now
          </div>
        </div>
        <Badge variant="outline" className="border-white/10 text-zinc-300 tabular-nums" data-testid="occupancy-total">
          <Radio className={"h-3 w-3 mr-1 " + (totalOnline > 0 ? "text-emerald-400 animate-pulse" : "text-zinc-500")} />
          {totalOnline} online
        </Badge>
      </div>

      {communities.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/10 p-6 text-center text-xs text-zinc-500" data-testid="occupancy-empty">
          {loading ? "Loading…" : "No communities in scope."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {communities.map((c) => (
            <div
              key={c.property_id}
              className={"rounded-md border p-3 relative overflow-hidden " +
                (c.online_count > 0 ? "border-emerald-500/30 bg-emerald-500/[0.03]" : "border-white/10 bg-white/[0.02]")}
              data-testid={`occupancy-community-${c.property_id}`}
            >
              <div className="flex items-start gap-2 mb-2">
                {c.hero_image ? (
                  <img src={c.hero_image} alt="" className="h-10 w-14 rounded-md object-cover border border-white/10 shrink-0" />
                ) : (
                  <div className="h-10 w-14 rounded-md bg-white/[0.03] border border-white/10 flex items-center justify-center shrink-0">
                    <MapPin className="h-4 w-4 text-zinc-500" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-200 truncate">{c.name}</div>
                  <div className="text-[11px] text-zinc-500">{c.units || "—"} units</div>
                </div>
                <Badge variant="outline"
                       className={"tabular-nums " + (c.online_count > 0 ? "border-emerald-500/40 text-emerald-300" : "border-white/10 text-zinc-500")}
                       data-testid={`occupancy-count-${c.property_id}`}>
                  {c.online_count}
                </Badge>
              </div>
              {c.online_count > 0 && (
                <div className="flex flex-wrap gap-1">
                  {Object.entries(c.by_role || {}).map(([role, count]) => (
                    <Badge key={role} variant="outline"
                           className={"text-[10px] tabular-nums " + (ROLE_TONE[role] || "border-white/10 text-zinc-400")}
                           data-testid={`occupancy-role-${c.property_id}-${role}`}>
                      {count} {role}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default OccupancyMapCard;
